import { useState } from "react";

import useShare, { ShareOptions } from "@/shared/hooks/useShare";
import useCopyToClipboard from "@/shared/hooks/useCopyToClipboard";
import { Button, ButtonProps, Tooltip } from "@heroui/react";
import { CheckIcon, CopyIcon, ShareFatIcon } from "@phosphor-icons/react";
import { ICON_SIZE } from "@/config/constants";

export interface ShareButtonProps extends ButtonProps {
  data: ShareOptions;
}

function ShareButton({ data, size = "md", ...rest }: ShareButtonProps) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { canCopy, copy } = useCopyToClipboard();
  const { canShare, share } = useShare();

  const renderShareButton = () => (
    <ShareButtonBase aria-label="Share" size={size} onPress={() => share(data)} {...rest}>
      <ShareFatIcon size={ICON_SIZE[size]} />
    </ShareButtonBase>
  );

  const renderCopyButton = () => {
    if (!canCopy) {
      return null;
    }

    const textParts: string[] = [];
    if (data.title) {
      textParts.push(data.title);
    }
    if (data.text) {
      textParts.push(data.text);
    }
    if (data.url) {
      textParts.push(data.url);
    }

    return (
      <Tooltip content="Copied" isOpen={isTooltipOpen}>
        <ShareButtonBase
          aria-label="Copy"
          size={size}
          onPress={() => {
            copy(textParts.join("\n\n"));
            setIsTooltipOpen(true);
            setTimeout(() => {
              setIsTooltipOpen(false);
            }, 1500);
          }}
          {...rest}
        >
          {isTooltipOpen ? <CheckIcon size={ICON_SIZE[size]} /> : <CopyIcon size={ICON_SIZE[size]} />}
        </ShareButtonBase>
      </Tooltip>
    );
  };

  return canShare ? renderShareButton() : renderCopyButton();
}

export default ShareButton;

function ShareButtonBase({ size, ...rest }: ButtonProps) {
  return <Button isIconOnly size={size} variant="light" {...rest}></Button>;
}
